import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Helmet } from "react-helmet-async";
import PublicLayout from "@/components/layout/PublicLayout";
import QuoteForm from "@/components/shared/QuoteForm";
import ReviewBadge from "@/components/social-proof/ReviewBadge";

import ProjectCarousel from "@/components/shared/ProjectCarousel";
import { COMPANY, SERVICE_AREAS, PUBLIC_EMAIL } from "@/lib/constants";
import { useVisitorTracking } from "@/hooks/useVisitorTracking";

// ContactPage + ContactPoint schema
const contactSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ContactPage",
      "@id": "https://obriencontractingusa.com/contact/#contactpage",
      "url": "https://obriencontractingusa.com/contact",
      "name": "Contact O'Brien Contracting",
      "isPartOf": { "@id": "https://obriencontractingusa.com/#website" },
      "about": { "@id": "https://obriencontractingusa.com/#localbusiness" }
    },
    {
      "@type": "ContactPoint",
      "@id": "https://obriencontractingusa.com/#contactpoint",
      "telephone": "+1-239-919-4485",
      "contactType": "customer service",
      "areaServed": "US-FL",
      "availableLanguage": ["en"]
    }
  ]
};

const Contact = () => {
  const { trackCTAClick } = useVisitorTracking();

  const breadcrumbs = [
    { name: "Home", url: "https://obriencontractingusa.com/" },
    { name: "Contact", url: "https://obriencontractingusa.com/contact" }
  ];

  return (
    <PublicLayout
      title="Contact Us"
      description={`Contact O'Brien Contracting for a free roofing estimate. Call ${COMPANY.phoneFormatted} or fill out our form. Serving all major South Florida counties.`}
      breadcrumbs={breadcrumbs}
    >
      <Helmet>
        <link rel="alternate" hrefLang="es" href="https://obriencontractingusa.com/es/contacto" />
        <link rel="alternate" hrefLang="en" href="https://obriencontractingusa.com/contact" />
        <script type="application/ld+json">
          {JSON.stringify(contactSchema)}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="py-16 bg-hero">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary-foreground mb-4">
            Contact Us
          </h1>
          <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto">
            Ready to get started? Get in touch for a free, no-obligation estimate.
          </p>
        </div>
      </section>

      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 [&>*]:min-w-0">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                Get In Touch
              </h2>
              <p className="text-muted-foreground mb-8">
                Have questions about your roofing project? We're here to help. 
                Reach out by phone, email, or fill out our contact form.
              </p>

              <div className="space-y-6">
                <a 
                  href={COMPANY.phoneHref}
                  className="flex items-start gap-4 p-4 rounded-xl bg-secondary hover:bg-secondary/80 transition-colors"
                  data-track-cta="contact_phone"
                  onClick={() => trackCTAClick('Call Now', 'contact_info')}
                >
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">Call Us</div>
                    <div className="text-accent font-bold text-lg">{COMPANY.phoneFormatted}</div>
                  </div>
                </a>

                {PUBLIC_EMAIL && (
                  <a
                    href={`mailto:${PUBLIC_EMAIL}`}
                    className="flex items-start gap-4 p-4 rounded-xl bg-secondary hover:bg-secondary/80 transition-colors"
                    data-track-cta="contact_email"
                    onClick={() => trackCTAClick('Email Now', 'contact_info')}
                  >
                    <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <div className="font-semibold text-foreground">Email Us</div>
                      <div className="text-muted-foreground break-all">{PUBLIC_EMAIL}</div>
                    </div>
                  </a>
                )}

                <div className="flex items-start gap-4 p-4 rounded-xl bg-secondary">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">Office Location</div>
                    <div className="text-muted-foreground">{COMPANY.address.full}</div>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-secondary">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">Business Hours</div>
                    <div className="text-muted-foreground space-y-1">
                      <div>Monday - Friday: 7:00 AM - 6:00 PM</div>
                      <div>Saturday: 8:00 AM - 2:00 PM</div>
                      <div>Sunday: Closed</div>
                      <div className="text-accent font-medium">24/7 Emergency Service Available</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Areas */}
              <div className="mt-8">
                <h3 className="text-lg font-heading font-bold text-foreground mb-4">
                  Service Areas
                </h3>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_AREAS.map((area) => (
                    <span 
                      key={area.slug}
                      className="px-3 py-1 rounded-full bg-accent/10 text-accent text-sm font-medium"
                    >
                      {area.name}
                    </span>
                  ))}
                  <span className="px-3 py-1 rounded-full bg-secondary text-muted-foreground text-sm">
                    + Surrounding Areas
                  </span>
                </div>
              </div>

              {/* Google Map Embed for GMB Synergy */}
              <div className="mt-8">
                <h3 className="text-lg font-heading font-bold text-foreground mb-4">
                  Our Location
                </h3>
                <div className="rounded-xl overflow-hidden border border-border">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3552.357397476569!2d-82.19878642388791!3d27.082024652792697!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x64f03ccfde61d69b%3A0x69628c245f428a0!2sO'Brien%20Contracting!5e0!3m2!1sen!2sus!4v1786071196305!5m2!1sen!2sus"
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="O'Brien Contracting Location - North Port, FL"
                  />
                </div>
              </div>
            </div>

            {/* Quote Form */}
            <div>
              <ReviewBadge variant="detailed" className="mb-6" />
              <QuoteForm sourcePage="contact-page" requireAddress />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects Section */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <span className="text-accent font-semibold text-sm uppercase tracking-wider">
              Our Work
            </span>
            <h2 className="text-3xl font-heading font-bold text-foreground mt-2">
              Recent Projects
            </h2>
            <p className="text-muted-foreground mt-2">
              See the quality craftsmanship we deliver to South Florida homeowners
            </p>
          </div>
          <ProjectCarousel />
        </div>
      </section>

      
    </PublicLayout>
  );
};

export default Contact;
